// Email ↔ SFTP delivery toggle plus the destination picker, shared by the
// "send now" and schedule dialogs.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HardDriveUpload, Mail } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function useDestinations(enabled = true) {
  const [list, setList] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    api.get("/report-hub/sftp/destinations").then((r) => setList(r.data.destinations || [])).catch(() => setList([]));
  }, [enabled]);
  return list;
}

export function ChannelToggle({ value, onChange, testid = "channel" }) {
  const opts = [["email", "Email", Mail], ["sftp", "SFTP upload", HardDriveUpload]];
  return (
    <div className="inline-flex rounded-md border border-slate-200 p-0.5" data-testid={`${testid}-toggle`}>
      {opts.map(([v, label, Icon]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
                className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                  value === v ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-50"}`}>
          <Icon className="h-3.5 w-3.5" /> {label}
        </button>
      ))}
    </div>
  );
}

export function DestinationSelect({ value, onChange, destinations, testid = "destination" }) {
  const { has } = useAuth();
  const list = destinations || [];
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">SFTP destination</Label>
      {destinations === null ? (
        <div className="h-9 rounded-md border border-slate-200 bg-slate-50" />
      ) : list.length === 0 ? (
        <p className="rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={`${testid}-none`}>
          No SFTP destination exists yet.{" "}
          {has("settings_sftp.edit")
            ? <Link to="/settings?tab=sftp" className="font-medium underline">Add one in Settings → SFTP destinations</Link>
            : "Ask an admin to add one under Settings → SFTP destinations."}
        </p>
      ) : (
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger className="h-9" data-testid={`${testid}-select`}><SelectValue placeholder="Pick a destination" /></SelectTrigger>
          <SelectContent>
            {list.map((d) => (
              <SelectItem key={d.destination_id} value={d.destination_id} data-testid={`${testid}-option-${d.destination_id}`}>
                {d.name}{d.is_sandbox ? " · preview" : ""} <span className="text-slate-400">— {d.username}@{d.host}{d.remote_dir ? `:${d.remote_dir}` : ""}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {value && list.find((d) => d.destination_id === value)?.last_test_ok === false && (
        <p className="text-[11px] text-rose-600" data-testid={`${testid}-warning`}>The last connection test on this destination failed.</p>
      )}
    </div>
  );
}
